// colors.ts — модуль для работы с цветами преподавателей

declare global {
  interface Window {
    ajaxurl?: string;
    teacherTabColorsNonce?: string;
  }
}

export type TeacherTabColor = {
  teacher_name: string;
  teacher_slug: string;
  tab_color: string;
  tab_text_color: string;
};

const STORAGE_KEY = 'teacherTabColors';
const STORAGE_TIMESTAMP_KEY = 'teacherTabColorsTimestamp';
const CACHE_TTL_MS = 60 * 60 * 1000; // 1 час

/**
 * Проверяет, нужно ли обновлять кэш (старше 1 часа)
 */
function isCacheExpired(): boolean {
  const ts = localStorage.getItem(STORAGE_TIMESTAMP_KEY);
  if (!ts) return true;
  const now = Date.now();
  return now - parseInt(ts, 10) > CACHE_TTL_MS;
}

/**
 * Загружает цвета преподавателей через AJAX и сохраняет в localStorage
 */
export async function fetchAndStoreTeacherTabColors(): Promise<TeacherTabColor[] | null> {
  if (!window.ajaxurl || !window.teacherTabColorsNonce) return null;
  try {
    const formData = new FormData();
    formData.append('action', 'get_teacher_tab_colors');
    formData.append('security', window.teacherTabColorsNonce);
    const response = await fetch(window.ajaxurl, {
      method: 'POST',
      body: formData,
    });
    const data = await response.json();
    //console.log('[fetchAndStoreTeacherTabColors] Ответ сервера:', data);

    // Исправлено: массив лежит в data.data.data
    const colorsArray = data?.data?.data;
    if (data.success && Array.isArray(colorsArray)) {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(colorsArray));
      localStorage.setItem(STORAGE_TIMESTAMP_KEY, String(Date.now()));
      //console.log('[fetchAndStoreTeacherTabColors] Сохраняем в localStorage:', colorsArray);
      return colorsArray;
    }
    return null;
  } catch (e) {
    //console.error('[fetchAndStoreTeacherTabColors] Ошибка:', e);
    return null;
  }
}

/**
 * Получает массив цветов из localStorage
 */
export function getTeacherTabColors(): TeacherTabColor[] {
  const raw = localStorage.getItem(STORAGE_KEY);
  if (!raw) {
    //console.warn('[getTeacherTabColors] Нет данных в localStorage');
    return [];
  }
  try {
    const parsed = JSON.parse(raw);
    //console.log('[getTeacherTabColors] Прочитано из localStorage:', parsed);
    return parsed;
  } catch (e) {
    //console.error('[getTeacherTabColors] Ошибка парсинга:', e);
    return [];
  }
}

/**
 * Получает цвета по slug или name
 */
export function getTabColorByTeacher(teacher: {slug?: string, name?: string}): {tab_color: string, tab_text_color: string} | null {
  const colors = getTeacherTabColors();
  let found = null;
  if (teacher.slug) {
    found = colors.find(c => c.teacher_slug === teacher.slug);
    if (found) {
      //console.log('[getTabColorByTeacher] Найдено по slug:', teacher.slug, found);
      return {tab_color: found.tab_color, tab_text_color: found.tab_text_color};
    }
  }
  if (teacher.name) {
    found = colors.find(c => c.teacher_name === teacher.name);
    if (found) {
      //console.log('[getTabColorByTeacher] Найдено по name:', teacher.name, found);
      return {tab_color: found.tab_color, tab_text_color: found.tab_text_color};
    }
  }
  //console.warn('[getTabColorByTeacher] Не найдено:', teacher);
  return null;
}

/**
 * Получает цвета таба для курса (аналог логики PHP card.php)
 */
export function getTabColorsForCourse(course: any): { tab_color: string; tab_text_color: string } {
  // Проверяем наличие эксперта
  const expert = Array.isArray(course.experts) && course.experts.length > 0 ? course.experts[0] : null;
  let colors = null;
  if (expert && expert.slug) {
    colors = getTabColorByTeacher({ slug: expert.slug });
  }
  if ((!colors || !colors.tab_color) && expert && expert.name) {
    colors = getTabColorByTeacher({ name: expert.name });
  }
  return {
    tab_color: (colors && colors.tab_color) || '#000',
    tab_text_color: (colors && colors.tab_text_color) || '#fff',
  };
}

// Инициализация: если данных нет или кэш устарел — загружаем
if (!localStorage.getItem(STORAGE_KEY) || isCacheExpired()) {
  fetchAndStoreTeacherTabColors();
}
